// Kategori ağacı aç/kapa durumu — ekran başına, kullanıcı başına localStorage'da kalıcı (isteğe bağlı;
// depolama kapalıysa yalnız oturum içi). Boş küme = tüm bantlar açık (varsayılan).
import { useCallback, useEffect, useRef, useState } from "react";
import type { KpiResolvedIndicator } from "@mahir/shared";
import { allBandIds, loadCollapsed, saveCollapsed, toggleBand } from "../lib/kpi-tree-list";
import { useAuthStore } from "../stores/auth-store";

export interface TreeCollapsedApi {
  collapsed: ReadonlySet<string>;
  toggle: (id: string) => void;
  setBand: (id: string, collapsed: boolean) => void;
  collapseAll: () => void;
  expandAll: () => void;
}

export function useTreeCollapsed(
  screen: string,
  indicators: readonly KpiResolvedIndicator[],
  seed?: readonly string[],
): TreeCollapsedApi {
  const userId = useAuthStore((s) => s.user?.id) ?? "anon";
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => {
    const stored = loadCollapsed(userId, screen);
    return stored.size === 0 && seed ? new Set(seed) : stored;
  });
  // auth sonradan hidrate olursa ("anon" → gerçek kimlik) kalıcı durumu yeniden yükle
  const loadedFor = useRef(`${userId}|${screen}`);
  useEffect(() => {
    const key = `${userId}|${screen}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    const stored = loadCollapsed(userId, screen);
    setCollapsed(stored.size === 0 && seed ? new Set(seed) : stored);
  }, [userId, screen]);
  const commit = useCallback(
    (next: ReadonlySet<string>) => {
      setCollapsed(next);
      saveCollapsed(userId, screen, next);
    },
    [userId, screen],
  );
  return {
    collapsed,
    toggle: (id) => commit(toggleBand(collapsed, id)),
    setBand: (id, c) => {
      if (collapsed.has(id) !== c) commit(toggleBand(collapsed, id));
    },
    collapseAll: () => commit(new Set(allBandIds(indicators))),
    expandAll: () => commit(new Set()),
  };
}
